import { useEffect, useState, type FormEvent } from 'react';
import { adminApi } from '../../lib/api-client';
import { useAuth } from '../../hooks/useAuth';
import { useStaffMe } from '../../hooks/useStaffMe';
import { InstantTime, instantFromWallClock, useEffectiveTimeZone, wallClockOf } from '../../lib/time-format';
import { ActionIcon } from '../../components/ActionIcon';

/**
 * Geo-override page (promoted to its own "Geo" tab at 5d-3b UAT Fix 3).
 *
 * Per ADR-009 the staff-pre-auth Lambda hard-blocks sign-ins from outside
 * AU. A super_admin can grant a time-bounded exception against a specific
 * staffSub for documented travel. The override is read from the Staff
 * table fields geoOverrideUntil / geoOverrideBy / geoOverrideReason via
 * GET /admin/geo-overrides (super_admin + nexa_admin).
 *
 * Role split (5d-3b UAT Fix 3):
 *   - super_admin — full: view active overrides, grant new, revoke existing.
 *   - nexa_admin  — view only: active overrides are shown; grant/revoke UI is
 *     hidden. The grant/revoke API endpoints stay super_admin-only server-side
 *     (ADR-009/ADR-012), so this is defence-in-depth, not the sole gate.
 *
 * Audit logging happens on the admin-api side — every grant and revoke
 * goes through a structured console.info line per LRS allowlist. The
 * portal does not need its own audit trail.
 */
interface StaffRow {
  cognitoSub: string;
  staffId: string;
  email: string;
  role: string;
  geoOverrideUntil?: string;
  geoOverrideBy?: string;
  geoOverrideReason?: string;
}

export function GeoOverridesPage({ embedded = false }: { embedded?: boolean }) {
  const { status } = useAuth();
  const me = useStaffMe();
  const [staff, setStaff] = useState<StaffRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [grantTarget, setGrantTarget] = useState<StaffRow | null>(null);

  // Only super_admin can grant / revoke; nexa_admin gets a read-only view
  // (5d-3b UAT Fix 3). Defaults closed while /admin/me resolves.
  // UAT fix: geo-override is staff:manage (per-target) = {nexa_admin, super_admin} for a non-super
  // staff target (super-staff targets are super-only — enforced server-side by the §4.3 forbid).
  const canMutate = me.status === 'loaded' && (me.me.role === 'super_admin' || me.me.role === 'nexa_admin');

  useEffect(() => {
    if (status !== 'authenticated') return;
    void refresh();
  }, [status]);

  async function refresh(): Promise<void> {
    setError(null);
    try {
      const resp = await adminApi<{ staff: StaffRow[] }>('/admin/geo-overrides');
      setStaff(resp.staff);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load staff list.');
    }
  }

  async function revoke(row: StaffRow): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await adminApi(`/admin/staff/${encodeURIComponent(row.cognitoSub)}/geo-override`, {
        method: 'DELETE',
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  }

  if (status === 'loading' || staff === null) {
    const Wrapper = embedded ? 'section' : 'main';
    return (
      <Wrapper className={embedded ? 'portal-geo-embedded' : 'portal-page'}>
        {!embedded && <h1>Geo overrides</h1>}
        {error
          ? <div role="alert" className="portal-error">{error}</div>
          : <p>Loading…</p>}
      </Wrapper>
    );
  }

  // Defensive guard (5d-3b UAT Fix 4): only render rows with a real email.
  // The handler already drops non-METADATA / email-less rows, but guarding
  // here too means a malformed row can never render as a ghost entry.
  const realStaff = staff.filter((s) => Boolean(s.email));
  const withOverride = realStaff.filter((s) => Boolean(s.geoOverrideUntil));
  const withoutOverride = realStaff.filter((s) => !s.geoOverrideUntil);

  const Wrapper = embedded ? 'section' : 'main';
  return (
    <Wrapper className={embedded ? 'portal-geo-embedded' : 'portal-page'}>
      {!embedded && <h1>Geo overrides</h1>}
      <p className="portal-help">
        ADR-009 mandates AU-only staff sign-in. {canMutate
          ? 'Use this page to grant a time-bounded exception when a staff member is travelling. Every grant and revoke is audit-logged server-side.'
          : 'This is a read-only view of active geo overrides. Granting and revoking exceptions is restricted to Nexa Admin and Super Admin roles.'}
      </p>
      <p className="portal-help">
        Geo restrictions apply to staff accounts only. Subscriber accounts (Owners,
        Members, Resellers) sign in via the subscriber portal which has no
        geographic restriction.
      </p>
      {error && <div role="alert" className="portal-error">{error}</div>}

      <section>
        <h2>Active overrides</h2>
        {withOverride.length === 0 ? (
          <p className="portal-help">No active overrides.</p>
        ) : (
          <table className="portal-table">
            <thead>
              <tr>
                <th>Staff</th><th>Email</th><th>Until</th><th>Reason</th><th>Granted by</th>
                {canMutate && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {withOverride.map((row) => (
                <tr key={row.cognitoSub}>
                  <td><code>{row.staffId}</code></td>
                  <td>{row.email}</td>
                  <td><InstantTime iso={row.geoOverrideUntil} /></td>
                  <td>{row.geoOverrideReason}</td>
                  <td><code>{row.geoOverrideBy}</code></td>
                  {canMutate && (
                    <td>
                      <ActionIcon act="remove" icon="remove" label="Revoke" tip={`Revoke ${row.staffId}'s override — sign-in is geo-restricted again`}
                        disabled={busy} onClick={() => revoke(row)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {canMutate && (
      <section>
        <h2>Grant a new override</h2>
        {grantTarget ? (
          <GrantForm
            target={grantTarget}
            busy={busy}
            onCancel={() => setGrantTarget(null)}
            onGranted={async () => {
              setGrantTarget(null);
              await refresh();
            }}
            setError={setError}
            setBusy={setBusy}
          />
        ) : (
          <table className="portal-table">
            <thead><tr><th>Staff</th><th>Email</th><th>Role</th><th>Actions</th></tr></thead>
            <tbody>
              {withoutOverride.map((row) => (
                <tr key={row.cognitoSub}>
                  <td><code>{row.staffId}</code></td>
                  <td>{row.email}</td>
                  <td>{row.role}</td>
                  <td>
                    <ActionIcon act="assign" icon="plus" label="Grant override" tip={`Grant ${row.staffId} an override — sign in from anywhere until a date`}
                      onClick={() => setGrantTarget(row)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      )}
    </Wrapper>
  );
}

function GrantForm({
  target, busy, onCancel, onGranted, setError, setBusy,
}: {
  target: StaffRow;
  busy: boolean;
  onCancel: () => void;
  onGranted: () => Promise<void>;
  setError: (s: string | null) => void;
  setBusy: (b: boolean) => void;
}) {
  // Default to 48 hours out — most travel exceptions don't need more. The input has no zone, so its
  // value is the staff member's own wall clock (lib/time-format.tsx), and is read back in that zone.
  const zone = useEffectiveTimeZone();
  const defaultUntil = wallClockOf(Date.now() + 48 * 60 * 60 * 1000, zone) ?? '';
  const [until, setUntil] = useState(defaultUntil);
  const [reason, setReason] = useState('');

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const expiresAt = instantFromWallClock(until, zone);
      if (!expiresAt) throw new Error('Enter the date and time the override expires.');
      await adminApi(`/admin/staff/${encodeURIComponent(target.cognitoSub)}/geo-override`, {
        method: 'POST',
        body: JSON.stringify({
          expiresAt: expiresAt.toISOString(),
          reason: reason.trim(),
        }),
      });
      await onGranted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Grant failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} aria-busy={busy} className="portal-wizard-form">
      <p className="portal-help">
        Granting override for <code>{target.staffId}</code> ({target.email}).
      </p>
      <label>
        Expires at
        <input
          type="datetime-local"
          required
          value={until}
          onChange={(e) => setUntil(e.target.value)}
        />
      </label>
      <label>
        Reason
        <textarea
          required
          minLength={5}
          maxLength={500}
          value={reason}
          placeholder="e.g. Conference in San Francisco, May 18–22 2026"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <div className="portal-wizard-actions">
        <button type="submit" className="portal-btn" disabled={busy}>{busy ? 'Granting…' : 'Grant'}</button>
        <button type="button" onClick={onCancel} disabled={busy} className="portal-link-button-secondary">Cancel</button>
      </div>
    </form>
  );
}
